import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Calendar, MapPin, Users, Clock, ArrowLeft, Sparkles, Mic, FileText, CheckCircle, X, ArrowRight, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusPill({ status }) {
  const map = {
    pending:  { c: "#f39c12", label: "Bewerbung läuft" },
    approved: { c: "#2ecc71", label: "Zugelassen" },
    rejected: { c: "#ef4444", label: "Abgelehnt" },
  };
  const st = map[status] || map.pending;
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border"
      style={{ color: st.c, borderColor: `${st.c}66`, background: `${st.c}15` }}>
      {st.label}
    </span>
  );
}

function DeepDiveCard({ dd, onApply, index }) {
  const app = dd.my_application;
  const isFull = dd.max_attendees && dd.attendee_count >= dd.max_attendees;
  const isOnboard = dd.is_registered || app?.status === "approved";
  let action;
  if (isOnboard) {
    action = (
      <span data-testid={`deepdive-onboard-${dd.id}`} className="inline-flex items-center gap-1.5 px-4 h-10 border border-[#2ecc71]/50 text-[#2ecc71] text-[12px] uppercase tracking-wider font-outfit font-bold bg-[#2ecc71]/10">
        <CheckCircle size={13} /> Du bist dabei
      </span>
    );
  } else if (app?.status === "pending") {
    action = <StatusPill status="pending" />;
  } else if (app?.status === "rejected") {
    action = <StatusPill status="rejected" />;
  } else {
    action = (
      <Button onClick={() => onApply(dd)} data-testid={`deepdive-apply-${dd.id}`}
        className="bg-[#a855f7] text-white hover:bg-[#a855f7] hover:shadow-[0_0_32px_-4px_rgba(168,85,247,0.7)] hover:-translate-y-0.5 rounded-none h-11 px-5 font-outfit font-semibold text-[13px] tracking-wide transition-all">
        <FileText size={14} className="mr-1.5" /> Bewerben
        <ArrowRight size={14} className="ml-1.5" />
      </Button>
    );
  }
  return (
    <div className="group relative dd-reveal" style={{ animationDelay: `${Math.min(index * 110, 600)}ms` }} data-testid={`deepdive-card-${dd.id}`}>
      <div className="absolute -inset-px pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.25) 0%, transparent 40%, transparent 60%, rgba(0,229,255,0.15) 100%)" }} />
      <div className="relative bg-gradient-to-br from-white/[0.02] to-transparent border border-white/[0.07] h-full flex flex-col"
        style={{ borderLeft: "3px solid #a855f7" }}>
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-[#a855f7]/50 text-[#a855f7] bg-[#a855f7]/10">
              <Zap size={10} /> Deep Dive
            </span>
            <span className="flex items-center gap-1 text-[12px] text-white/50"><Clock size={11} />{dd.time} Uhr</span>
            {dd.max_attendees && (
              <span className="flex items-center gap-1 text-[12px] text-white/45">
                <Users size={11} />{dd.attendee_count}/{dd.max_attendees} Plätze
              </span>
            )}
          </div>
          <h3 className="font-outfit font-bold text-xl text-white tracking-tight leading-snug mb-3">{dd.title}</h3>
          <p className="text-[14px] text-white/60 leading-relaxed flex-1 whitespace-pre-wrap">{dd.description}</p>
          <div className="mt-5 pt-5 border-t border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
            {isFull && !isOnboard && !app ? (
              <span className="text-[12px] text-amber-400/80 font-outfit font-semibold uppercase tracking-wider">Ausgebucht · Bewerbung weiterhin möglich</span>
            ) : (
              <span className="text-[12px] text-white/40 font-outfit uppercase tracking-wider">
                {dd.speaker_ids?.length > 0 ? <><Mic size={11} className="inline mr-1 -mt-0.5" /> mit Speaker</> : "Offener Track"}
              </span>
            )}
            {action}
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplyDialog({ dd, open, onOpenChange, onDone }) {
  const [motivation, setMotivation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (motivation.trim().length < 10) { setErr("Bitte mindestens 10 Zeichen."); return; }
    setSubmitting(true); setErr("");
    try {
      await axios.post(`${API}/events/${dd.id}/apply`, { motivation: motivation.trim() }, { withCredentials: true });
      setMotivation("");
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Bewerbung fehlgeschlagen"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setMotivation(""); setErr(""); } onOpenChange(v); }}>
      <DialogContent className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none" data-testid="festival-apply-dialog">
        <div className="relative">
          <div className="absolute -inset-px pointer-events-none" style={{ background: "linear-gradient(135deg, #a855f740 0%, transparent 50%, rgba(0,229,255,0.2) 100%)" }} />
          <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <FileText size={13} className="text-[#a855f7]" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#a855f7]/90 font-outfit font-semibold">Deep-Dive Bewerbung</span>
              </div>
              <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">{dd?.title}</DialogTitle>
              <DialogDescription className="text-[13px] text-white/45 mt-1">Plätze sind begrenzt. Erzähl kurz, warum du dabei sein willst — der Speaker entscheidet.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-5">
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">Deine Motivation *</Label>
                <Textarea required minLength={10} maxLength={1200} value={motivation} onChange={(e) => setMotivation(e.target.value)}
                  placeholder="Was verbindet dich mit dem Thema? Was willst du mitnehmen?"
                  className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#a855f7]/60 focus:ring-0 min-h-[140px] text-[14px]"
                  data-testid="festival-motivation-input" />
                <p className="text-[11px] text-white/35 mt-1">{motivation.length}/1200</p>
              </div>
              {err && <p className="text-[13px] text-red-400">{err}</p>}
              <Button type="submit" disabled={submitting} data-testid="festival-submit-apply-btn" className="w-full bg-[#a855f7] text-white hover:bg-[#a855f7] hover:shadow-[0_0_32px_-4px_rgba(168,85,247,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all">
                {submitting ? "..." : "Bewerbung absenden"}
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FestivalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [applyDD, setApplyDD] = useState(null);
  const [notice, setNotice] = useState("");
  const [err, setErr] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Konnte Festival nicht laden"); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const festival = useMemo(() => events.find(e => e.id === id && e.event_kind === "festival_main"), [events, id]);
  const deepDives = useMemo(() =>
    events.filter(e => e.festival_parent_id === id && e.event_kind === "festival_deep_dive")
          .sort((a, b) => (a.time || "").localeCompare(b.time || "")),
    [events, id]
  );

  const dayStr = festival?.date ? new Date(festival.date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : "";
  const approvedCount = deepDives.filter(d => d.is_registered || d.my_application?.status === "approved").length;
  const pendingCount  = deepDives.filter(d => d.my_application?.status === "pending").length;
  const totalSeats    = deepDives.reduce((s, d) => s + (d.max_attendees || 0), 0);

  if (!festival && events.length > 0) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <X size={32} className="text-red-400/70 mx-auto mb-3" />
          <h1 className="font-outfit font-bold text-2xl mb-2">Festival nicht gefunden</h1>
          <p className="text-white/50 mb-6">{err || "Das Festival existiert nicht oder wurde archiviert."}</p>
          <Link to="/events" data-testid="festival-back-events" className="inline-flex items-center gap-2 px-5 h-11 border border-[#00e5ff]/40 text-[#00e5ff] font-outfit font-semibold text-[13px] uppercase tracking-wider hover:bg-[#00e5ff]/10"><ArrowLeft size={14} />Zurück zu Events</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[55%] h-[60%] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-[55%] h-[60%] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

          {/* Back link */}
          <button onClick={() => navigate("/events")} data-testid="festival-back-btn"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-white/45 hover:text-[#00e5ff] font-outfit font-semibold transition-colors mb-6">
            <ArrowLeft size={13} /> Alle Events
          </button>

          {/* Hero */}
          {festival && (
            <div className="relative border border-white/[0.08] overflow-hidden mb-10" data-testid="festival-hero">
              <div className="absolute inset-0">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_poster.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
                <video
                  autoPlay muted loop playsInline preload="metadata"
                  poster={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_poster.jpg`}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="festival-hero-video"
                >
                  <source src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_mobile.mp4`} type="video/mp4" media="(max-width: 768px)" />
                  <source src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg.mp4`} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/45" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
              <div className="relative p-7 sm:p-10 lg:p-12 min-h-[420px] flex flex-col justify-end">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-px bg-[#a855f7]" />
                  <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#a855f7] font-semibold">Festival</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-outfit font-bold border border-[#00e5ff]/50 text-[#00e5ff] bg-[#00e5ff]/10">
                    <Sparkles size={10} /> 3 Tracks
                  </span>
                </div>
                <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-white mb-5 max-w-3xl" data-testid="festival-title">
                  {festival.title}
                </h1>
                <p className="text-[16px] sm:text-lg text-white/65 max-w-2xl leading-relaxed mb-6 whitespace-pre-wrap">{festival.description}</p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/55 font-outfit">
                  <span className="inline-flex items-center gap-2"><Calendar size={13} className="text-[#00e5ff]" />{dayStr}</span>
                  <span className="inline-flex items-center gap-2"><Clock size={13} className="text-[#00e5ff]" />ab {festival.time} Uhr</span>
                  <span className="inline-flex items-center gap-2"><MapPin size={13} className="text-[#00e5ff]" />{festival.location}</span>
                </div>
              </div>
            </div>
          )}

          {notice && (
            <div className="mb-6 border-l-2 border-[#2ecc71] bg-[#2ecc71]/5 p-4 text-[13px] text-[#2ecc71]/90" data-testid="festival-notice">
              {notice}
              <button onClick={() => setNotice("")} className="ml-3 text-white/40 hover:text-white">×</button>
            </div>
          )}

          {/* Stats */}
          {festival && (
            <div className="grid grid-cols-3 gap-px bg-white/[0.05] mb-10 border border-white/[0.06]">
              {[
                { num: deepDives.length, label: "Deep-Dive Tracks", sub: "parallel am Festivaltag", color: "#a855f7" },
                { num: pendingCount, label: "Deine Bewerbungen", sub: pendingCount === 1 ? "läuft" : "laufen", color: "#f39c12" },
                { num: approvedCount || `${totalSeats}`, label: approvedCount ? "Zugelassen" : "Plätze gesamt", sub: approvedCount ? "du bist dabei" : "über alle Tracks", color: "#00e5ff" },
              ].map((s) => (
                <div key={s.label} className="bg-black px-4 sm:px-6 py-5">
                  <div className="font-outfit font-black text-3xl sm:text-4xl tracking-tighter" style={{ color: s.color }}>{s.num}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/55 font-outfit font-medium">{s.label}</div>
                  <div className="text-[12px] text-white/30 mt-0.5">{s.sub}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tracks */}
          <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-px bg-[#a855f7]/60" />
                <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#a855f7]/90 font-semibold">Tracks</span>
              </div>
              <h2 className="font-outfit font-black text-2xl sm:text-3xl tracking-tight text-white">Wähle deinen Deep Dive</h2>
              <p className="text-[14px] text-white/50 mt-1">Jeder Track hat begrenzte Plätze. Schreib dem Speaker kurz, warum du dabei sein willst.</p>
            </div>
            {user?.role === "admin" && (
              <Link to={`/events`} data-testid="festival-admin-back" className="text-[12px] uppercase tracking-wider text-white/40 hover:text-[#00e5ff] font-outfit font-semibold">Admin · zurück zur Eventliste</Link>
            )}
          </div>

          {deepDives.length === 0 ? (
            <div className="text-center py-20 border border-white/[0.06] bg-white/[0.01]" data-testid="festival-no-tracks">
              <Mic size={28} className="text-[#a855f7]/50 mx-auto mb-3" strokeWidth={1.3} />
              <p className="text-white/75 text-[15px] font-outfit font-semibold">Noch keine Deep-Dive-Tracks veröffentlicht</p>
              <p className="text-white/40 text-[13px] mt-1">Das Programm wird aktuell kuratiert — schau bald wieder rein.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="festival-tracks-grid">
              {deepDives.map((dd, i) => (
                <DeepDiveCard key={dd.id} dd={dd} index={i} onApply={setApplyDD} />
              ))}
            </div>
          )}

          {err && <p className="mt-6 text-red-400/80 text-[13px]">{err}</p>}
        </div>
      </div>

      <ApplyDialog dd={applyDD} open={!!applyDD} onOpenChange={(v) => !v && setApplyDD(null)}
        onDone={() => { setApplyDD(null); setNotice("Deine Bewerbung ist unterwegs! Der Speaker meldet sich per E-Mail, sobald eine Entscheidung getroffen wurde."); fetchAll(); }}
      />

      <style>{`
        .dd-reveal { opacity: 0; transform: translateY(18px); animation: dd-rise 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes dd-rise { to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .dd-reveal { animation: none; opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
